import { Controller } from "@hotwired/stimulus"

// Draws the goal timeline chart in from left to right the first time the step
// becomes visible, and again on every return visit. Every onboarding step is
// rendered up front and hidden, so connect() is far too early to animate:
// the onboarding controller dispatches onboarding:step-shown once the step is
// actually on screen, and that is what we listen for.
//
// The markup ships in its final state so the chart still reads without JS.
// This controller primes the hidden state itself, then plays. Under
// prefers-reduced-motion nothing is primed and the final state stands.
export default class extends Controller {
    static targets = ["line", "fill", "dot"]

    connect() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

        this.boundPlayIfShown = this.playIfShown.bind(this)
        window.addEventListener("onboarding:step-shown", this.boundPlayIfShown)

        this.reset()

        // Deep links land directly on this step, so it can already be visible
        // by the time the controller connects and no step-shown event follows.
        if (this.isVisible) this.play()
    }

    disconnect() {
        if (this.boundPlayIfShown) {
            window.removeEventListener("onboarding:step-shown", this.boundPlayIfShown)
        }
        if (this.timer) clearTimeout(this.timer)
    }

    playIfShown(event) {
        if (event.detail?.stepKey !== this.stepKey) return

        this.reset()
        // Let the reset paint before transitioning back, otherwise the browser
        // collapses both style changes into no animation at all.
        this.timer = setTimeout(() => this.play(), 30)
    }

    get stepKey() {
        return this.element.closest("[data-step-key]")?.dataset.stepKey
    }

    get isVisible() {
        return this.element.offsetParent !== null
    }

    reset() {
        this.lineTargets.forEach(line => {
            line.style.transition = "none"
            line.style.strokeDasharray = "1"
            line.style.strokeDashoffset = "1"
        })
        this.fillTargets.forEach(fill => {
            fill.style.transition = "none"
            fill.style.opacity = "0"
        })
        this.dotTargets.forEach(dot => {
            dot.style.transition = "none"
            dot.style.transformBox = "fill-box"
            dot.style.transformOrigin = "center"
            dot.style.opacity = "0"
            dot.style.transform = "scale(0.4)"
        })
    }

    play() {
        // Force a reflow so the primed state is committed before the
        // transitions are attached.
        this.element.offsetHeight // eslint-disable-line no-unused-expressions

        const drawDuration = 1400

        this.lineTargets.forEach(line => {
            line.style.transition = `stroke-dashoffset ${drawDuration}ms cubic-bezier(0.33, 0.9, 0.4, 1)`
            line.style.strokeDashoffset = "0"
        })

        this.fillTargets.forEach(fill => {
            fill.style.transition = `opacity 600ms ease ${drawDuration * 0.45}ms`
            fill.style.opacity = "1"
        })

        // The dots pop as the indigo line reaches each week marker.
        this.dotTargets.forEach((dot, index) => {
            const delay = drawDuration * (0.35 + index * 0.22)
            dot.style.transition = `opacity 220ms ease ${delay}ms, transform 320ms cubic-bezier(0.2, 1.4, 0.4, 1) ${delay}ms`
            dot.style.opacity = "1"
            dot.style.transform = "scale(1)"
        })
    }
}
